import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";

// Flatten the tree into rows, respecting collapsed branches.
function buildRows(root, collapsed) {
  const rows = [];
  const stack = [{ node: root, depth: 0 }];
  const walk = (node, depth) => {
    const hasChildren = !!(node.children && node.children.length);
    rows.push({ node, depth, hasChildren });
    if (hasChildren && !collapsed.has(node.id)) {
      node.children.forEach((c) => walk(c, depth + 1));
    }
  };
  const first = stack.pop();
  walk(first.node, first.depth);
  return rows;
}

export default function FamilyTree() {
  useReveal();
  const { c } = useSite();
  const t = c.familyTree;
  const { ui } = c;
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const goKin = () =>
    document.getElementById("kin")?.scrollIntoView({ behavior: "smooth" });

  const rows = buildRows(t.root, collapsed);

  return (
    <section id="tree" className="bg-ink py-28 sm:py-40">
      <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4 mb-7">
            <span className="eyebrow">{t.label}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl font-light leading-[1.02] text-parchment">
            {t.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-parchment/60 font-light">
            {t.intro}
          </p>
        </div>

        <div className="reveal mt-16 overflow-x-auto pb-4">
          <div className="min-w-0 space-y-4">
            {rows.map(({ node, depth, hasChildren }) => {
              const isCollapsed = collapsed.has(node.id);
              return (
                <div
                  key={node.id}
                  className="relative flex items-stretch"
                  style={{ marginLeft: depth * 22 }}
                >
                  {/* connector */}
                  {depth > 0 && (
                    <span
                      className="absolute top-8 bg-[var(--c-border)] h-px"
                      style={{ left: -24, width: 24 }}
                      aria-hidden
                    />
                  )}
                  {depth > 0 && (
                    <span
                      className="absolute bg-[var(--c-border)] w-px"
                      style={{ left: -24, top: -20, height: 52 }}
                      aria-hidden
                    />
                  )}

                  <div
                    className={`group relative flex items-start gap-4 w-full border px-5 py-4 transition-colors duration-300 ${
                      node.kin
                        ? "border-gold/50 bg-ink2 hover:bg-[var(--c-surface)] cursor-pointer"
                        : "border-[var(--c-border)] bg-ink2 hover:border-gold/40"
                    }`}
                    onClick={node.kin ? goKin : undefined}
                  >
                    {node.kin && (
                      <span className="mt-0.5 shrink-0">
                        <Crest size={26} />
                      </span>
                    )}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-label text-[0.6rem] uppercase tracking-[0.2em] text-gold">
                          {node.gen}
                        </span>
                        <span className="font-label text-[0.6rem] uppercase tracking-[0.16em] text-stone2">
                          {node.era}
                        </span>
                      </div>
                      <h3 className="font-display text-2xl sm:text-3xl font-medium text-parchment leading-tight mt-1">
                        {node.title}
                      </h3>
                      {node.epithet && (
                        <p className="font-display italic text-lg text-gold/90 -mt-0.5">
                          {node.epithet}
                        </p>
                      )}
                      <p className="mt-2 text-[0.98rem] leading-relaxed text-parchment/60 font-light max-w-xl">
                        {node.note}
                      </p>
                      {node.kin && (
                        <span className="inline-block mt-2 font-label text-[0.6rem] uppercase tracking-[0.2em] text-gold/80 underline underline-offset-4 group-hover:text-gold">
                          {ui.seeInKin}
                        </span>
                      )}
                    </div>

                    {hasChildren && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(node.id);
                        }}
                        className="shrink-0 mt-1 w-8 h-8 flex items-center justify-center border border-[var(--c-border)] text-gold hover:bg-gold hover:text-ink transition-colors"
                        aria-label={isCollapsed ? ui.expand : ui.collapse}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-300 ${
                            isCollapsed ? "-rotate-90" : ""
                          }`}
                        />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
